import { Permission } from '@bookora/shared';

import type { TourDef } from './types';

const hasTab = (search: string, tab: string): boolean =>
  new URLSearchParams(search).get('tab') === tab;

/**
 * «نتایج و صورت‌حساب» — the RaaS results tab (`/settings?tab=results`;
 * `/settings/results` redirects here). Four steps: what is counted (the
 * meter), the full list of counted patients, disputing a row, and the
 * subscription wallet the month is charged to. The finish goes to the cost
 * alert (`?focus=alert-budget`).
 *
 * Gated on `settings.billing` and the `raas` tour feature (the clinic is on
 * «نتیجه‌محور» — fail closed while plan access is unknown). Offered through the
 * ONE proactive budget like every page tour, so it is never offered while
 * impersonating or while `useGuidanceBusy()`.
 */
export const RAAS_RESULTS_TOUR: TourDef = {
  key: 'raas-results',
  version: 1,
  surface: 'page',
  title: 'نتایج و صورت‌حساب',
  hubDescription:
    'بیماران محاسبه‌شده در صورت‌حساب این ماه، سقف هزینه، اعتراض به یک ردیف و کیف پول اشتراک.',
  offer: {
    body: 'در چند قدم کوتاه نشونتون می‌دیم هزینهٔ نتیجه‌محور این ماه چطور محاسبه می‌شه و اگر بیماری اشتباه شمرده شد، چطور اعتراض کنید.',
  },
  route: {
    path: '/settings?tab=results',
    match: (pathname, search) => pathname === '/settings' && hasTab(search, 'results'),
  },
  requires: { all: [Permission.SETTINGS_BILLING] },
  requiresFeature: 'raas',
  autoOffer: true,
  maxSteps: 4,
  minSteps: 2,
  steps: [
    {
      id: 'meter',
      anchor: 'results.meter',
      placement: 'bottom',
      title: 'شمارش این ماه',
      body: 'هر بیماری که این ماه جلسه درمان انجام‌شده داشته یک بار شمرده می‌شه. مبلغ تا امروز، پیش‌بینی پایان ماه و فاصله تا سقف همین‌جاست.',
    },
    {
      id: 'charges',
      anchor: 'results.charges',
      placement: 'top',
      title: 'بیماران محاسبه‌شده در صورت‌حساب',
      body: 'فهرست کامل بیماران محاسبه‌شده در صورت‌حساب، با نمایش بخشی از نام بیمار و شمارهٔ کامل پرونده.',
    },
    {
      id: 'dispute',
      anchor: 'results.dispute',
      placement: 'auto',
      title: 'اعتراض به یک ردیف',
      body: 'اگر بیماری اشتباه شمرده شده، از همین‌جا اعتراض کنید. اگر اعتراض پذیرفته بشه، مبلغ اون ردیف به کیف پول اشتراک برمی‌گرده.',
    },
    {
      id: 'wallet',
      anchor: 'results.wallet',
      placement: 'top',
      title: 'کیف پول اشتراک',
      body: 'هزینه‌ها از کیف پول اشتراک کم می‌شه؛ قبل از تمام‌شدن موجودی باخبر می‌شید.',
    },
  ],
  finish: {
    label: 'تنظیم هشدار هزینه',
    to: '/settings/results?focus=alert-budget',
    requires: { all: [Permission.SETTINGS_BILLING] },
  },
};
